import * as THREE from "three";
import { fogMaskUniforms } from "./world/fog-mask";

/**
 * Shared 3-step toon gradient ramp. Sampled across the U axis by L·N during
 * MeshToonMaterial shading — three pixels = three discrete lighting bands.
 *
 * Bands picked at 0.30 / 0.65 / 1.00 brightness give a clear shadow → mid →
 * lit step pattern that reads as the hand-painted MOBA look (League / Dota).
 * Used by doodads, buildings, characters, and resource trees. Terrain stays
 * on `MeshLambertMaterial` per `docs/design/14-rendering.md` — toon banding
 * on a continuous heightmap surface looks wrong at this aesthetic.
 *
 * Cached as a module-level singleton so all toon materials share one texture
 * upload and one GPU sampler binding.
 */

/**
 * Whole-canopy occluder fade (2026-07-02). Replaces the "circle punched
 * through leaves" read with the WHOLE occluding doodad instance fading toward
 * ghost transparency. Per-INSTANCE (not per-material) so only the specific
 * tree/rock the hero is actually behind fades — driven by a per-instance
 * `instanceFade` InstancedBufferAttribute (see `world/doodads.ts`) that the
 * frame loop's camera→hero raycast scan writes into, not by these shared
 * uniforms. The uniforms here are just the two GLOBAL knobs: a master on/off
 * (debug-toggleable so before/after is live-comparable) and how far the
 * dither-discard density goes at full fade (never fully vanish — the "or its
 * canopy" ghost should still read as "something is there").
 */
export const canopyFadeUniforms = {
  uCanopyFadeEnabled: { value: 1 },       // 0/1 master gate
  uCanopyFadeMinOpacity: { value: 0.25 }, // dither keep-fraction at full fade (~0.15-0.35 target)
};

/**
 * Shared wind clock for alpha-cutout foliage (tree canopies + ferns). Driven once
 * per frame from main.ts (`foliageWind.uTime.value += dt`), bound into every
 * cutout material's vertex shader so the leaves sway like the grass field. Sway is
 * height-masked in local space so trunks stay anchored and only the canopy moves.
 */
export const foliageWind = { uTime: { value: 0 } };

/**
 * Minimum world-space height (metres) for a doodad to participate in the
 * see-through occlusion cutout. The Caveman hero stands ~1.8m; anything shorter
 * than this never meaningfully hides him at the locked ~40° MOBA pitch, so
 * stippling it away just distracts (playtest 2026-06-25: stone nodes / rocks /
 * low bushes shouldn't fade). Only meshes taller than the hero get the cutout.
 */
export const CUTOUT_MIN_HEIGHT = 2.5;

/**
 * True when a mesh, placed at `worldScale`, stands tall enough to occlude the
 * hero and so should keep the see-through cutout. Decided per source geometry
 * (× its placement scale) at load — short props opt out entirely rather than
 * gating per-fragment, which would leave a tall tree's trunk solid while its
 * canopy faded. `computeBoundingBox` is cheap + cached (bakeBaseAO already ran).
 */
export function occludesHero(geom: THREE.BufferGeometry, worldScale: number): boolean {
  geom.computeBoundingBox();
  const bb = geom.boundingBox;
  return !!bb && (bb.max.y - bb.min.y) * worldScale > CUTOUT_MIN_HEIGHT;
}

let gradient: THREE.DataTexture | null = null;

export function getToonGradient(): THREE.DataTexture {
  if (gradient) return gradient;
  const data = new Uint8Array([
    0x4d, 0x4d, 0x4d, 0xff,
    0xa6, 0xa6, 0xa6, 0xff,
    0xff, 0xff, 0xff, 0xff,
  ]);
  const tex = new THREE.DataTexture(data, 3, 1, THREE.RGBAFormat);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.needsUpdate = true;
  gradient = tex;
  return tex;
}

/**
 * Brighter "hero" gradient for player + creature materials. Shadow band
 * lifted to ~55% (vs world's 30%), mid band to ~80% (vs 65%). Net effect:
 * characters never sink fully into shadow regardless of which way the sun
 * is pointing — same trick League / Dota use to keep heroes readable in
 * dark biomes / fog-of-war / under cliffs. Doodads / buildings / terrain
 * stay on the standard `getToonGradient()` so the world keeps its full
 * dynamic range.
 */
let heroGradient: THREE.DataTexture | null = null;

export function getHeroGradient(): THREE.DataTexture {
  if (heroGradient) return heroGradient;
  const data = new Uint8Array([
    0x8c, 0x8c, 0x8c, 0xff,
    0xcc, 0xcc, 0xcc, 0xff,
    0xff, 0xff, 0xff, 0xff,
  ]);
  const tex = new THREE.DataTexture(data, 3, 1, THREE.RGBAFormat);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.needsUpdate = true;
  heroGradient = tex;
  return tex;
}

/**
 * Convert a GLTF-loaded source material (typically MeshStandardMaterial) into
 * a MeshToonMaterial preserving map / alpha / color / vertexColors / side. Do
 * NOT pass `flatShading: true` — every fragment lands in one band when toon
 * is combined with flat shading and the cel-shaded look disappears.
 *
 * `applyToonShader` runs with the caller's options (rim is always on; tint and
 * world-space noise are conditional). Callers that want noise on doodads /
 * buildings pass `{ noiseScale: ... }`; player / creature paths pass nothing.
 */
export function toToon(src: THREE.Material, opts: ToonShaderOptions = {}): THREE.MeshToonMaterial {
  const s = src as THREE.MeshStandardMaterial;
  // Preserve the source's vertexColors flag. Forcing it on for assets that
  // ship without a color attribute (Quaternius character / creature packs)
  // makes three.js read uninitialized `vColor` and multiplies the diffuse to
  // black — characters render as silhouettes. Doodad / nature meshes that
  // we want AO on already have vertex colors enabled in their source so AO
  // bake reaches the shader for them.
  const out = new THREE.MeshToonMaterial({
    map: s.map,
    color: s.color,
    vertexColors: s.vertexColors,
    transparent: s.transparent,
    opacity: s.opacity,
    alphaTest: s.alphaTest,
    side: s.side,
    gradientMap: opts.heroLighting ? getHeroGradient() : getToonGradient(),
  });
  out.name = s.name;
  out.alphaMap = s.alphaMap;
  applyToonShader(out, opts);
  return out;
}

/**
 * Compose toon shader hooks via `onBeforeCompile`:
 *  - **Fresnel rim** (always): brightens silhouette edges so 3D objects read
 *    cleanly against background. `rim = pow(1 - N·V, rimPower)`. Tunable color
 *    / strength / power.
 *  - **Luminance-preserving tint** (optional): when `tintColor` is set, recolors
 *    every fragment to the tint while preserving the source diffuse's brightness
 *    pattern. Used by `world.ts:applyTint` for autumn-flora variants.
 *
 * Both hooks compose into one `onBeforeCompile` so a tinted material still gets
 * the rim — earlier this was split into two callbacks and tinting overrode rim.
 */
export interface ToonShaderOptions {
  rimColor?: number;     // hex 0xRRGGBB, default 0xffffff (warm white rim)
  rimStrength?: number;  // 0..1; default 0.10
  rimPower?: number;     // exponent on (1 - N·V); higher = tighter edge; default 4.0
  tintColor?: number;    // hex 0xRRGGBB; when set, recolors via luminance preserve
  tintMix?: number;      // 0..1 blend toward tint; default 0.92
  tintLift?: number;     // multiplier on tint × luminance; default 1.85
  // World-space fbm noise modulation on diffuseColor — gives sub-meter texture
  // grain to large flat surfaces (tree canopies, building walls) the same way
  // terrain.ts pulls texture out of vertex-colored ground. `noiseScale` is the
  // world-XZ sample frequency (higher = tighter grain). 0 / undefined = off.
  noiseScale?: number;
  noiseStrength?: number; // ±N% lightness mod; default 0.12
  // When true, use the brighter "hero" gradient ramp (~55% shadow band) so
  // the material never sinks fully into shadow. Used for player + creature
  // characters; world geometry keeps the standard ramp.
  heroLighting?: boolean;
  // When true, this primitive is eligible for the whole-canopy occluder fade
  // (dither-discard driven by the per-instance `instanceFade` attribute + the
  // shared `canopyFadeUniforms`). Set on doodads/trees tall enough to occlude
  // the hero — see `occludesHero`/`CUTOUT_MIN_HEIGHT` above.
  cutout?: boolean;
  // When true, this material dissolves its fragments where the shared fog mask
  // says the ground is never-explored — for doodads/resource nodes, so the
  // unexplored frontier hides everything standing on it (not just tints the
  // dirt). Reads the shared `fogMaskUniforms` (world/fog-mask.ts); stipple-
  // dissolves IN as an area is scouted, then stays revealed forever (map memory).
  fogMask?: boolean;
}

/**
 * Runtime alternative to a Blender vertex-AO bake. Darkens vertex colors at
 * the bottom of a mesh's local bounding box so doodads / rocks / buildings
 * read with a soft contact-shadow gradient where they meet the ground —
 * League / Dota's hand-painted maps bake this into their textures; we fake
 * it on the per-vertex color channel.
 *
 * Mutates the geometry's `color` attribute in place. Idempotent via a WeakSet
 * — calling on the same geometry twice is a no-op. Geometry is shared across
 * InstancedMesh instances for a given asset, so one bake covers every clone.
 *
 * Skipped for SkinnedMesh / character geo: feet vertices animate off the ground
 * and a baked-darker base would follow them through the air.
 *
 * `strength`: how dark the very base goes (0.4 → 60% brightness at y_min).
 * `falloff`: where the AO band ends as a fraction of bbox height (0.20 →
 * AO covers bottom 20% of the mesh, fully lit above).
 */
const bakedAOGeometries = new WeakSet<THREE.BufferGeometry>();

export function bakeBaseAO(
  geom: THREE.BufferGeometry,
  strength = 0.4,
  falloff = 0.20,
): void {
  if (bakedAOGeometries.has(geom)) return;
  bakedAOGeometries.add(geom);

  const positions = geom.attributes.position as THREE.BufferAttribute | undefined;
  if (!positions) return;

  geom.computeBoundingBox();
  const box = geom.boundingBox;
  if (!box) return;
  const minY = box.min.y;
  const range = box.max.y - minY;
  if (range < 0.001) return;  // degenerate / flat mesh — skip

  // Initialise a white color attribute if the source mesh has none. Lambert /
  // Toon will only sample vertex colors when `vertexColors: true` on the
  // material, but writing them costs nothing and lets us darken even meshes
  // that didn't ship with vertex colors.
  let colors = geom.attributes.color as THREE.BufferAttribute | undefined;
  if (!colors) {
    const arr = new Float32Array(positions.count * 3);
    arr.fill(1.0);
    colors = new THREE.BufferAttribute(arr, 3);
    geom.setAttribute("color", colors);
  }

  for (let i = 0; i < positions.count; i++) {
    const y = positions.getY(i);
    const ratio = (y - minY) / range;
    if (ratio >= falloff) continue;
    const t = ratio / falloff;
    const eased = t * t * (3 - 2 * t);  // smoothstep
    const factor = 1 - (1 - eased) * strength;
    colors.setXYZ(i, colors.getX(i) * factor, colors.getY(i) * factor, colors.getZ(i) * factor);
  }
  colors.needsUpdate = true;
}

/**
 * Inverted-hull outline pass for SkinnedMesh characters / creatures.
 *
 * Walks `root`, finds every SkinnedMesh, and for each one creates a sibling
 * SkinnedMesh that:
 *  - shares the source's geometry (no extra vertex data)
 *  - shares the source's skeleton + bindMatrix (animations drive both copies
 *    automatically — no per-frame sync needed)
 *  - uses a black `MeshBasicMaterial` with `side: BackSide`, so only the
 *    inside-facing fragments of the inflated hull render. Their projection is
 *    the silhouette behind the original mesh.
 *  - inflates `transformed` along the post-skinning `objectNormal` by
 *    `outlineWidth` units (object space) so the hull pokes out by a thin
 *    constant distance regardless of pose.
 *
 * Hooks at `<skinning_vertex>` because both `transformed` and `objectNormal`
 * are skin-transformed by then (`objectNormal` post-skin requires USE_SKINNING,
 * which three.js auto-defines for any SkinnedMesh material).
 *
 * Outlines are added to each SkinnedMesh's parent so they inherit the same
 * world transform; original render order is preserved (outline draws first
 * via natural depth, original mesh draws over it inside the silhouette).
 *
 * `outlineWidth` is a **world-space** thickness (in world units / metres). The
 * push happens in LOCAL geometry space, so it gets magnified by EVERY scale
 * between the skinned mesh and the world: both any baked-in node scale (some
 * glTFs — e.g. the polyperfect Sabertooth/DireWolf — wrap their mesh in a 100×
 * node) AND `root.scale` (the registry `def.scale`, e.g. Cave Bear 2.0). We
 * divide the requested width by that full accumulated scale so the rendered
 * outline is a constant world-space thickness regardless of creature size —
 * otherwise a 2× bear gets a 2× outline (the "bear's outline looks wrong /
 * fox looks fine because it's smaller" bug) and a 100×-node mesh becomes a blob.
 */
export function addCharacterOutline(root: THREE.Object3D, outlineWidth = 0.035): void {
  type Pending = { parent: THREE.Object3D; outline: THREE.SkinnedMesh };
  const pending: Pending[] = [];

  root.traverse((node) => {
    const skinned = node as THREE.SkinnedMesh;
    if (!skinned.isSkinnedMesh) return;
    if (!skinned.parent) return;

    // Accumulate every scale from the skinned mesh up to AND INCLUDING `root`
    // (root holds def.scale, set before this runs and not yet parented to the
    // scene). Dividing the world-space width by this product makes the rendered
    // outline a constant thickness in world units across all creatures.
    let accumScale = 1;
    for (let p: THREE.Object3D | null = skinned; p; p = p === root ? null : p.parent) accumScale *= p.scale.x;
    const width = outlineWidth / accumScale;

    const outlineMat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      side: THREE.BackSide,
      // Outlines should fade with fog the same way the world does.
      fog: true,
    });
    outlineMat.onBeforeCompile = (shader) => {
      shader.uniforms.uOutlineWidth = { value: width };
      shader.vertexShader =
        "uniform float uOutlineWidth;\n" +
        shader.vertexShader.replace(
          "#include <skinning_vertex>",
          `#include <skinning_vertex>
          // After this chunk, both 'transformed' and 'objectNormal' are in
          // skin-transformed local space — adding objectNormal * width pushes
          // the hull out along the animated silhouette.
          transformed += objectNormal * uOutlineWidth;`,
        );
    };

    const outline = new THREE.SkinnedMesh(skinned.geometry, outlineMat);
    outline.name = `${skinned.name || "skinned"}:outline`;
    outline.bind(skinned.skeleton, skinned.bindMatrix);
    outline.castShadow = false;
    outline.receiveShadow = false;
    outline.frustumCulled = skinned.frustumCulled;

    pending.push({ parent: skinned.parent, outline });
  });

  // Add after traversal so we don't mutate the tree we're walking.
  for (const { parent, outline } of pending) parent.add(outline);
}

export function applyToonShader(mat: THREE.Material, opts: ToonShaderOptions = {}): void {
  const rimColor = new THREE.Color(opts.rimColor ?? 0xffffff);
  // Defaults dialed back from the first pass — strength 0.18 / power 2.5 gave
  // tree canopies a snowy frosted look because spherical surfaces have lots
  // of mid-range (1 - N·V) fragments. Tighter power (4.0) confines the rim
  // to the actual silhouette edge; lower strength (0.10) keeps the lift
  // subtle, more "painted outline" than "glow."
  // Alpha-cutout foliage (the EXPLORER tree/fern leaf cards, alphaMode=MASK →
  // alphaTest>0) is a mesh of double-sided cards with huge silhouette-edge area;
  // the Fresnel rim lights every grazing-angle leaf edge and frosts the whole
  // canopy white. Suppress the rim for cutout materials by default (callers can
  // still force a value). Solid props/rocks keep the painterly edge lift.
  const matIsCutout = (mat as THREE.MeshToonMaterial).alphaTest !== undefined
    && (mat as THREE.MeshToonMaterial).alphaTest > 0;
  const rimStrength = opts.rimStrength ?? (matIsCutout ? 0 : 0.10);
  const rimPower = opts.rimPower ?? 4.0;
  const hasTint = opts.tintColor !== undefined;
  const tintColor = hasTint ? new THREE.Color(opts.tintColor!) : null;
  const tintMix = opts.tintMix ?? 0.92;
  const tintLift = opts.tintLift ?? 1.85;
  const hasNoise = opts.noiseScale !== undefined && opts.noiseScale > 0;
  const noiseScale = opts.noiseScale ?? 0;
  const noiseStrength = opts.noiseStrength ?? 0.12;
  const hasCutout = opts.cutout === true;
  const hasFogMask = opts.fogMask === true;

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uRimColor = { value: rimColor };
    shader.uniforms.uRimStrength = { value: rimStrength };
    shader.uniforms.uRimPower = { value: rimPower };

    let fragHeader = "uniform vec3 uRimColor;\nuniform float uRimStrength;\nuniform float uRimPower;\n";

    if (hasCutout) {
      // Bind the SHARED uniform objects (not fresh ones) so a single per-frame
      // update in main.ts drives every tree material at once.
      shader.uniforms.uCanopyFadeEnabled = canopyFadeUniforms.uCanopyFadeEnabled;
      shader.uniforms.uCanopyFadeMinOpacity = canopyFadeUniforms.uCanopyFadeMinOpacity;
      fragHeader +=
        "uniform float uCanopyFadeEnabled;\nuniform float uCanopyFadeMinOpacity;\n" +
        "varying float vDoodadFade;\n";

      // Every cutout-eligible doodad primitive is built as an InstancedMesh with
      // a per-instance `instanceFade` attribute (world/doodads.ts) — 0 when the
      // instance isn't occluding the hero, ramping to 1 while it is (written by
      // the frame loop's camera→hero raycast scan). Pass it through to the
      // fragment stage as a varying; declaring the attribute is harmless even on
      // the (currently unused) non-instanced path since three.js just leaves an
      // unbound generic attribute at its default (0.0).
      shader.vertexShader =
        "attribute float instanceFade;\nvarying float vDoodadFade;\n" +
        shader.vertexShader.replace(
          "#include <project_vertex>",
          `#include <project_vertex>
          #ifdef USE_INSTANCING
            vDoodadFade = instanceFade;
          #else
            vDoodadFade = 0.0;
          #endif`,
        );
    }

    if (hasTint && tintColor) {
      shader.uniforms.uTintColor = { value: tintColor };
      shader.uniforms.uTintMix = { value: tintMix };
      shader.uniforms.uTintLift = { value: tintLift };
      fragHeader +=
        "uniform vec3 uTintColor;\nuniform float uTintMix;\nuniform float uTintLift;\n";
    }

    if (hasNoise) {
      shader.uniforms.uNoiseScale = { value: noiseScale };
      shader.uniforms.uNoiseStrength = { value: noiseStrength };
      fragHeader +=
        "uniform float uNoiseScale;\nuniform float uNoiseStrength;\n" +
        "varying vec3 vToonWorldPos;\n" +
        // Bilinear value-noise + 3-octave fbm. Function names prefixed `tn_` so
        // they don't collide with the terrain's own onBeforeCompile injection.
        "float tn_hash21(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }\n" +
        "float tn_value(vec2 p) { vec2 i = floor(p); vec2 f = fract(p); float a = tn_hash21(i); float b = tn_hash21(i + vec2(1.0,0.0)); float c = tn_hash21(i + vec2(0.0,1.0)); float d = tn_hash21(i + vec2(1.0,1.0)); vec2 u = f*f*(3.0-2.0*f); return mix(mix(a,b,u.x), mix(c,d,u.x), u.y); }\n" +
        "float tn_fbm(vec2 p) { return tn_value(p) * 0.55 + tn_value(p * 4.0) * 0.30 + tn_value(p * 16.0) * 0.15; }\n";

      // Vertex-side: pass world position to the fragment. Compute via
      // modelMatrix × instanceMatrix × position so each instance picks up its
      // own world-XZ noise coords (instanced doodads don't share the same
      // noise pattern globally — but the same instance reads the same noise
      // across reloads, since it's deterministic on world position).
      shader.vertexShader =
        "varying vec3 vToonWorldPos;\n" +
        shader.vertexShader.replace(
          "#include <project_vertex>",
          `#include <project_vertex>
          #ifdef USE_INSTANCING
            vToonWorldPos = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xyz;
          #else
            vToonWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;
          #endif`,
        );
    }

    if (hasFogMask) {
      // Bind the SHARED fog-mask uniform objects (not fresh ones) so the single
      // per-frame update in fog.ts drives every fog-maskable material at once —
      // same pattern as canopyFadeUniforms above.
      shader.uniforms.uFogMask = fogMaskUniforms.uFogMask;
      shader.uniforms.uFogMaskOrigin = fogMaskUniforms.uFogMaskOrigin;
      shader.uniforms.uFogMaskExtent = fogMaskUniforms.uFogMaskExtent;
      shader.uniforms.uFogMaskEnabled = fogMaskUniforms.uFogMaskEnabled;
      fragHeader +=
        "uniform sampler2D uFogMask;\nuniform vec2 uFogMaskOrigin;\n" +
        "uniform float uFogMaskExtent;\nuniform float uFogMaskEnabled;\n" +
        "varying vec2 vFogWorldXZ;\n";

      // Dedicated world-XZ varying — do NOT reuse vToonWorldPos, which only
      // exists on the hasNoise path. Re-includes the project_vertex chunk so it
      // composes cleanly with the other #include <project_vertex> replaces.
      shader.vertexShader =
        "varying vec2 vFogWorldXZ;\n" +
        shader.vertexShader.replace(
          "#include <project_vertex>",
          `#include <project_vertex>
          #ifdef USE_INSTANCING
            vFogWorldXZ = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xz;
          #else
            vFogWorldXZ = (modelMatrix * vec4(transformed, 1.0)).xz;
          #endif`,
        );
    }

    // Wind sway for alpha-cutout foliage (tree canopies + ferns) — same idea as
    // the grass field. Per-instance phase from world position so neighbours sway
    // out of sync; height-masked in LOCAL space (squared) so trunks stay anchored
    // and only the upper canopy moves. H≈6 anchors typical trunks; tall canopies
    // (local y up to ~22) get the full sway.
    if (matIsCutout) {
      shader.uniforms.uWindTime = foliageWind.uTime;
      shader.vertexShader =
        "uniform float uWindTime;\n" +
        shader.vertexShader.replace(
          "#include <begin_vertex>",
          `#include <begin_vertex>
          #ifdef USE_INSTANCING
            float wPhase = instanceMatrix[3].x * 0.18 + instanceMatrix[3].z * 0.15;
          #else
            float wPhase = modelMatrix[3].x * 0.18 + modelMatrix[3].z * 0.15;
          #endif
          float wMask = clamp(transformed.y / 6.0, 0.0, 1.0);
          wMask *= wMask;
          float wSway1 = sin(wPhase + uWindTime * 1.3) * 0.35;
          float wSway2 = sin(wPhase * 1.7 + uWindTime * 0.8) * 0.18;
          transformed.x += (wSway1 + wSway2) * wMask;
          transformed.z += cos(wPhase * 0.9 + uWindTime * 1.1) * 0.22 * wMask;`,
        );
    }

    // Compose post-color-fragment hooks. Order is important:
    // 1. Tint recolor first — sets the hue while preserving source luminance.
    // 2. Noise modulation second — adds sub-meter lightness variation on
    //    whichever color (original or tinted) survived step 1.
    let postColor = "";
    if (hasTint) {
      postColor +=
        "float tintLum = dot(diffuseColor.rgb, vec3(0.299, 0.587, 0.114));\n" +
        "diffuseColor.rgb = mix(diffuseColor.rgb, uTintColor * tintLum * uTintLift, uTintMix);\n";
    }
    if (hasNoise) {
      postColor +=
        "float toonDetail = tn_fbm(vToonWorldPos.xz * uNoiseScale);\n" +
        "diffuseColor.rgb *= mix(1.0 - uNoiseStrength, 1.0 + uNoiseStrength, toonDetail);\n";
    }

    let frag = fragHeader + shader.fragmentShader;

    if (hasCutout) {
      // Dither-discard fragments of an occluding doodad instance once the
      // whole-canopy fade (vDoodadFade, per-instance — see canopyFadeUniforms
      // above) has ramped it toward ghost transparency. The
      // interleaved-gradient-noise threshold makes a fine stipple instead of a
      // hard cut — order-independent, no transparency sorting needed (which
      // InstancedMesh can't do per-instance anyway). `uCanopyFadeMinOpacity`
      // caps how far the fade goes so a fully-faded canopy still reads as
      // "something is there", never fully vanishes.
      //
      // A screen-space "hero-porthole circle" cutout used to run alongside
      // this (in-front-of-player + within on-screen radius, independent of
      // which instance). Deleted 2026-07-02: buildings never opted into it
      // (the "backup for buildings" justification didn't hold up — see
      // building-render.ts, no `cutout: true`), and it stacked with this fade
      // to re-punch a hole through canopies the fade already handled, plus a
      // unit's own x-ray silhouette could double-tint through it. Whole-canopy
      // fade is now the sole occlusion-readability mechanism.
      frag = frag.replace(
        "#include <clipping_planes_fragment>",
        `#include <clipping_planes_fragment>
        float cutDitherBase = fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
        if (uCanopyFadeEnabled > 0.5 && vDoodadFade > 0.001) {
          float canopyOpacity = mix(1.0, uCanopyFadeMinOpacity, vDoodadFade);
          if ((1.0 - canopyOpacity) > cutDitherBase) discard;
        }`,
      );
    }

    if (hasFogMask) {
      // Fog-of-war dissolve. Sample the shared mask's explored channel (G) at
      // this fragment's world-XZ and stipple-discard where the ground has never
      // been scouted. Runs right after clipping planes — before lighting — so
      // discarded frontier fragments cost the least. Nested replaces of
      // #include <clipping_planes_fragment> compose fine with the hasCutout
      // block above (each re-includes the chunk).
      //
      // G is temporally eased in fog.ts, so scouting a new area ramps its
      // doodads' reveal 0→1 over ~200ms and they stipple-dissolve IN; once
      // explored, G stays 1 (reveal→1) forever — SC2-style map memory, the
      // terrain is remembered even after vision is lost. Interleaved-gradient
      // noise gives an order-independent stipple (no transparency sort needed,
      // which InstancedMesh can't do per-instance). `fm`/`uFogMask` prefixes
      // keep this distinct from the tn_/rim/tint/noise/canopy injections.
      frag = frag.replace(
        "#include <clipping_planes_fragment>",
        `#include <clipping_planes_fragment>
        if (uFogMaskEnabled > 0.5) {
          vec2 fmUv = (vFogWorldXZ - uFogMaskOrigin) / uFogMaskExtent;
          float fmExplored = texture2D(uFogMask, fmUv).g;
          float fmReveal = smoothstep(0.10, 0.72, fmExplored);
          float fmDither = fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
          if (fmReveal < fmDither) discard;
        }`,
      );
    }

    if (postColor) {
      frag = frag.replace(
        "#include <color_fragment>",
        `#include <color_fragment>\n${postColor}`,
      );
    }

    // Rim injection runs BEFORE tone mapping so the rim term gets ACES along
    // with the rest of the frame. `vNormal` + `vViewPosition` are declared by
    // the standard pars chunks for Toon / Lambert / Standard / Phong.
    frag = frag.replace(
      "#include <tonemapping_fragment>",
      `float rim = pow(1.0 - clamp(dot(normalize(vNormal), normalize(vViewPosition)), 0.0, 1.0), uRimPower);
      gl_FragColor.rgb += uRimColor * rim * uRimStrength;
      #include <tonemapping_fragment>`,
    );

    shader.fragmentShader = frag;
  };
  mat.needsUpdate = true;
}
